import { useState } from 'react';
import { X, Search, CheckCircle, AlertCircle, Copy } from 'lucide-react';

interface OutputKeyFinderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectOutputKey: (outputKey: string) => void;
}

export const OutputKeyFinderModal = ({ isOpen, onClose, onSelectOutputKey }: OutputKeyFinderModalProps) => {
  const [falResponse, setFalResponse] = useState('');
  const [foundPaths, setFoundPaths] = useState<Array<{ path: string; description: string; type: string }>>([]);
  const [error, setError] = useState<string | null>(null);

  const analyzeFalResponse = () => {
    try {
      setError(null);
      const parsed = JSON.parse(falResponse);
      const paths: Array<{ path: string; description: string; type: string }> = [];

      // Helper function to find all possible output paths
      const findPaths = (obj: any, currentPath: string = '') => {
        if (!obj || typeof obj !== 'object') return;

        Object.keys(obj).forEach(key => {
          const value = obj[key];
          const newPath = currentPath ? `${currentPath}.${key}` : key;

          // Check if this is a media URL or array of media
          if (typeof value === 'string' && value.includes('http') && (value.includes('.jpg') || value.includes('.png') || value.includes('.mp4') || value.includes('.mp3'))) {
            paths.push({
              path: newPath,
              description: `Direct URL (${value.slice(-20)}...)`,
              type: value.includes('.mp4') ? 'video' : value.includes('.mp3') ? 'audio' : 'image'
            });
          } else if (Array.isArray(value) && value.length > 0) {
            // Check if array contains media objects
            if (value[0] && typeof value[0] === 'object' && value[0].url) {
              paths.push({
                path: newPath,
                description: `Array of ${value.length} media object(s) with .url property`,
                type: 'media_array'
              });
            } else if (typeof value[0] === 'string' && value[0].includes('http')) {
              paths.push({
                path: newPath,
                description: `Array of ${value.length} URL string(s)`,
                type: 'url_array'
              });
            }
          } else if (value && typeof value === 'object' && value.url) {
            // Single media object with URL
            paths.push({
              path: newPath,
              description: `Single media object with .url property`,
              type: 'media_object'
            });
          }

          // Recurse into nested objects
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            findPaths(value, newPath);
          }
        });
      };

      findPaths(parsed);

      if (paths.length === 0) {
        setError('No media output paths found in the response. Make sure you pasted a complete FAL API response with image, video, or audio outputs.');
      } else {
        setFoundPaths(paths);
      }
    } catch (err) {
      setError('Invalid JSON format. Please paste a valid FAL API response.');
    }
  };

  const handleSelectPath = (path: string) => {
    onSelectOutputKey(path);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-base/90 z-modal-content flex items-center justify-center p-4">
      <div className="bg-surface border border-subtle rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-subtle">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
              <Search className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-th-primary">Find Output Key</h2>
              <p className="text-sm text-th-secondary">
                Paste FAL API response to automatically detect output paths
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-th-secondary hover:text-th-primary transition-colors rounded-lg hover:bg-overlay"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Instructions */}
          <div className="bg-elevated border border-subtle rounded-lg p-4">
            <h3 className="text-th-primary font-semibold mb-2">📋 How to Use</h3>
            <ol className="text-th-secondary text-sm space-y-1 list-decimal list-inside">
              <li>Run your FAL workflow and copy the complete JSON response</li>
              <li>Paste the response in the text area below</li>
              <li>Click "Analyze Response" to find all possible output paths</li>
              <li>Select the correct path for your media output</li>
            </ol>
          </div>

          {/* FAL Response Input */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-th-primary">
              Paste FAL API Response
            </label>
            <textarea
              value={falResponse}
              onChange={(e) => setFalResponse(e.target.value)}
              placeholder={`Example FAL response:
{
  "images": [
    {
      "url": "https://fal.media/files/...",
      "file_name": "output.jpg",
      "content_type": "image/jpeg",
      "file_size": 1024000
    }
  ],
  "seed": 12345,
  "has_nsfw_concepts": [false],
  "prompt": "A beautiful landscape"
}`}
              rows={12}
              className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-field placeholder-th-tertiary focus:border-brand focus:outline-none resize-none font-mono text-sm"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-th-secondary">
                Paste the complete JSON response from your FAL API call
              </p>
              <button
                onClick={analyzeFalResponse}
                disabled={!falResponse.trim()}
                className="flex items-center space-x-2 px-4 py-2 bg-accent text-surface rounded-lg font-medium hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Search className="w-4 h-4" />
                <span>Analyze Response</span>
              </button>
            </div>
          </div>

          {/* Found Paths */}
          {foundPaths.length > 0 && (
            <div className="bg-overlay border border-subtle rounded-lg p-4 space-y-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-th-secondary" />
                <h3 className="text-th-primary font-semibold">Found Output Paths ({foundPaths.length})</h3>
              </div>
              
              <div className="space-y-3">
                {foundPaths.map((pathInfo, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 bg-elevated rounded-lg border border-subtle hover:border-muted transition-all"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <code className="text-th-primary font-mono bg-muted/30 px-2 py-1 rounded">
                          {pathInfo.path}
                        </code>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          pathInfo.type === 'image' ? 'bg-muted/40 text-th-primary' :
                          pathInfo.type === 'video' ? 'bg-overlay text-th-secondary' :
                          pathInfo.type === 'audio' ? 'bg-elevated border border-subtle text-th-tertiary' :
                          'bg-muted/25 text-th-secondary'
                        }`}>
                          {pathInfo.type}
                        </span>
                      </div>
                      <p className="text-th-secondary text-sm">
                        {pathInfo.description}
                      </p>
                      <div className="mt-2 p-2 bg-surface rounded border border-subtle/30">
                        <p className="text-xs text-th-secondary mb-1">Complete access path:</p>
                        <code className="text-th-secondary font-mono text-xs">
                          falResult.{pathInfo.path}
                        </code>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => copyToClipboard(pathInfo.path)}
                        className="p-2 text-th-secondary hover:text-th-primary transition-colors rounded hover:bg-overlay"
                        title="Copy path"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleSelectPath(pathInfo.path)}
                        className="px-4 py-2 bg-accent text-surface rounded-lg font-medium hover:bg-accent/90 transition-all"
                      >
                        Use This Path
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-overlay border border-subtle rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-status-error" />
                <span className="text-status-error font-medium">Analysis Failed</span>
              </div>
              <p className="text-status-error/70 text-sm mt-2">{error}</p>
            </div>
          )}

          {/* Examples */}
          <div className="bg-elevated/50 border border-subtle/30 rounded-lg p-4">
            <h4 className="text-th-primary font-medium mb-3">💡 Common Output Patterns</h4>
            <div className="bg-status-error/10 border border-status-error/20 rounded p-3 mb-4">
              <p className="text-status-error text-xs font-medium">⚠️ Output Key Format Rules</p>
              <ul className="text-th-secondary text-xs mt-2 space-y-1 list-disc list-inside">
                <li>Use only the container name (e.g., "images", not "images[0]")</li>
                <li>Do NOT include array indexing like "[0]" or "[1]"</li>
                <li>Do NOT include ".url" - the system adds this automatically</li>
                <li>Examples: "images", "video", "audio", "image_url"</li>
              </ul>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-th-secondary">Single image:</span>
                <code className="text-brand font-mono">images</code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-th-secondary">Single video:</span>
                <code className="text-brand font-mono">video</code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-th-secondary">Single audio:</span>
                <code className="text-brand font-mono">audio</code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-th-secondary">Multiple images:</span>
                <code className="text-brand font-mono">images</code>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-subtle">
          <div className="text-sm text-th-secondary">
            {foundPaths.length > 0 ? 'Select the correct output path for your model' : 'Paste your FAL response above to get started'}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-elevated border border-subtle text-th-secondary rounded-lg font-medium hover:text-th-primary hover:border-brand/50 transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};